import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { storeNonCdnUrl } from '../hooks/useAddUrlToAirtable';
import { logger } from '../utils/logger';

const bucketNameCDN = import.meta.env.VITE_S3_BUCKET_NAME_CDN;
const bucketRegion = import.meta.env.VITE_S3_BUCKET_REGION;
const MediaRoot = import.meta.env.VITE_MEDIA_ROOT;

if (!bucketNameCDN) {
  throw new Error('CDN Bucket name not provided in .env (VITE_S3_BUCKET_NAME_CDN)');
}

if (!bucketRegion) {
  throw new Error('Bucket region not provided in .env (VITE_S3_BUCKET_REGION)');
}

if (!MediaRoot) {
  throw new Error('Media Root not provided in .env (VITE_MEDIA_ROOT)');
}

async function uploadFile(awsKeys, file: File, path = 'general', progressCb?: (percent: number) => void) {
  const fileName = `${Date.now()}-${file.name}`;
  const key = `${path}/${fileName}`;
  let mainBucketLocation = '';

  // Create S3 client
  const s3Client = new S3Client({
    region: bucketRegion,
    credentials: {
      accessKeyId: awsKeys?.AccessKeyId!,
      secretAccessKey: awsKeys?.SecretKey!,
      sessionToken: awsKeys?.SessionToken!,
    }
  });

  // 2. Upload to CDN bucket with retry logic - CDN or nothing approach
  let cdnUploadSuccess = false;
  let cdnError = undefined as Error | undefined;
  const maxRetries = 3;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      logger.debug(`[CDN BUCKET] Starting upload to ${bucketNameCDN} (Attempt ${attempt}/${maxRetries})`);

      // Set content type and encoding based on file type for CDN upload
      const contentType = file.type || 'application/octet-stream';
      const params: any = {
        Bucket: bucketNameCDN!,
        Key: key,
        Body: file,
        ContentType: contentType,
        // Explicitly set the checksum algorithm to CRC32
        ChecksumAlgorithm: "CRC32"
      };
      
      // Add specific parameters for PDF files
      if (file.type === 'application/pdf') {
        logger.debug(`[CDN BUCKET] Uploading PDF with specific parameters`);
        params.ContentDisposition = 'inline';
        params.ContentEncoding = 'utf-8';
      }
      
      const cdnUpload = new Upload({
        client: s3Client,
        params
      });

      // Add progress tracking for CDN upload too
      if (typeof progressCb === 'function') {
        cdnUpload.on('httpUploadProgress', (progress) => {
          if (progress.loaded && progress.total) {
            const loaded = Math.floor((progress.loaded / progress.total) * 100);
            logger.debug(`[CDN BUCKET] Upload progress: ${loaded}%`);
            progressCb(loaded);
          }
        });
      }

      const cdnData = await cdnUpload.done();
      mainBucketLocation = cdnData.Location || '';
      logger.debug(`[CDN BUCKET] Upload successful. Location: ${mainBucketLocation || 'No location returned'}`);
      cdnUploadSuccess = true;
      break; // Exit the retry loop if successful

    } catch (error) {
      cdnError = error as Error;
      logger.error(`[CDN BUCKET] Upload attempt ${attempt}/${maxRetries} failed:`, cdnError);
      
      if (attempt < maxRetries) {
        // Wait before retrying (exponential backoff)
        const delayMs = Math.pow(2, attempt) * 1000;
        logger.debug(`[CDN BUCKET] Retrying in ${delayMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
  }

  // If CDN upload failed after all retries, throw an error
  if (!cdnUploadSuccess) {
    logger.error(`[CDN BUCKET] All ${maxRetries} upload attempts failed for file: ${file.name}`);
    throw new Error(`Failed to upload file to CDN bucket after ${maxRetries} attempts. Please try again or contact support.`);
  }

  logger.debug(`Upload process completed for file: ${file.name}`);
  
  // Create the CDN path
  const cdnRoot = import.meta.env.VITE_CDN_ROOT;
  const relativePath = getRelativePathFromAbsolute(mainBucketLocation);
  
  // Log paths for debugging
  logger.debug(`[File Upload] CDN path: ${cdnRoot}${relativePath}`);
  
  // Return the relative path - getMediaAbsolutePath will handle using the CDN path
  return relativePath;
}

function getRelativePathFromAbsolute(path: string) {
  return path.replace(/^(https?:\/\/.*?)(?=\/)/, '');
}

type Format = 'pdf';

async function downloadFile(_url: string, format?: Format) {
  if (!_url) {
    throw new Error('Url not provided');
  }

  const url = getMediaAbsolutePath(_url);

  return fetch(url)
    .then(resp => resp.blob())
    .then(blob => {
      const localUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = localUrl;

      const regex = /\.\w+$/gm;
      const defaultFileName = `File-${Date.now()}.${format}`;
      const fileNameFromUrl = url.split('/').pop();
      const decodedFileName = fileNameFromUrl ? decodeURIComponent(fileNameFromUrl) : defaultFileName;

      a.download = regex.test(url) ? decodedFileName : defaultFileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(localUrl);
    })
    .catch(e => {
      logger.debug(`[e]`, e);
      throw e;
    });
}

async function downloadFileWithName(name: string, _url: string, format?: Format) {
  if (!_url) {
    throw new Error('Url not provided');
  }

  const url = getMediaAbsolutePath(_url);

  return fetch(url)
    .then(resp => resp.blob())
    .then(blob => {
      const localUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = localUrl;

      const regex = /\.\w+$/gm;
      const defName = `${name}.${format}`;
      a.download = regex.test(url) ? url.split('/').pop() ?? defName : defName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(localUrl);
    })
    .catch(e => {
      logger.debug(`[e]`, e);
      throw e;
    });
}

function getMediaAbsolutePath(path: string | undefined) {
  if (!path) return '';
  if (/^(https?:\/\/|blob:)/.test(path)) return path;

  // Normalize path: ensure it begins with a single '/'
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // Use CDN URL for retrieving files
  const cdnRootRaw = import.meta.env.VITE_CDN_ROOT;
  if (cdnRootRaw) {
    const cdnRoot = String(cdnRootRaw).replace(/\/+$/, '');
    const finalUrl = `${cdnRoot}${normalizedPath}`;
    
    // Debug logging
    console.log('[getMediaAbsolutePath] Input:', path);
    console.log('[getMediaAbsolutePath] CDN Root:', cdnRoot);
    console.log('[getMediaAbsolutePath] Normalized Path:', normalizedPath);
    console.log('[getMediaAbsolutePath] Final URL:', finalUrl);
    
    return finalUrl;
  }
  
  // Fallback to MediaRoot if CDN_ROOT is not available
  const mediaRootClean = String(MediaRoot).replace(/\/+$/, '');
  const mediaRootPath = `${mediaRootClean}${normalizedPath}`;
  
  console.log('[getMediaAbsolutePath] Using MediaRoot fallback:', mediaRootPath);
  
  // Store the non-CDN path in localStorage for later sending to Airtable
  storeNonCdnUrl(mediaRootPath);
  
  return mediaRootPath;
}

export {
  uploadFile,
  downloadFile,
  downloadFileWithName,
  getMediaAbsolutePath,
};
